import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, MessageSquarePlus } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import type { FeedbackCategory } from '@regatta-replay/types';

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-select a category when opening from context (e.g., "data_error" from results page) */
  defaultCategory?: FeedbackCategory;
}

const CATEGORIES: { value: FeedbackCategory; label: string }[] = [
  { value: 'data_error', label: 'Data Error' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other', label: 'Other' },
];

export function FeedbackDialog({ open, onOpenChange, defaultCategory }: FeedbackDialogProps) {
  const location = useLocation();
  const { isAuthenticated, user } = useAuthStore();
  const [category, setCategory] = useState<FeedbackCategory>(defaultCategory ?? 'other');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function resetForm() {
    setCategory(defaultCategory ?? 'other');
    setSubject('');
    setBody('');
    setEmail('');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) return;

    setSubmitting(true);
    try {
      await api.post('/feedback', {
        category,
        subject: subject.trim(),
        body: body.trim(),
        pageUrl: window.location.href,
        ...(!isAuthenticated && email.trim() && { email: email.trim() }),
      });

      toast({
        title: 'Feedback submitted',
        description: 'Thank you! We\'ll review your feedback shortly.',
      });

      resetForm();
      onOpenChange(false);
    } catch {
      // toast handled by api helper
    } finally {
      setSubmitting(false);
    }
  }

  // Reset category when defaultCategory changes (e.g., opening from "Report data error")
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen && defaultCategory) {
      setCategory(defaultCategory);
    }
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus size={18} />
            Send Feedback
          </DialogTitle>
          <DialogDescription>
            Found an error in the results? Have a feature idea? Let us know.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fb-category">Category</Label>
            <select
              id="fb-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-subject">Subject</Label>
            <Input
              id="fb-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief summary of the issue"
              required
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fb-body">Description</Label>
            <Textarea
              id="fb-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Describe what you noticed, what you expected, or what you'd like to see..."
              rows={4}
              required
            />
          </div>

          {!isAuthenticated && (
            <div className="space-y-2">
              <Label htmlFor="fb-email">Email (optional)</Label>
              <Input
                id="fb-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
              <p className="text-xs text-slate-400">
                So we can follow up if needed.
              </p>
            </div>
          )}

          <div className="text-xs text-slate-400 bg-slate-50 p-2 rounded">
            Page: {location.pathname}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!subject.trim() || !body.trim() || submitting}
              className="bg-brand-blue !text-navy-900 hover:bg-navy-900 hover:!text-white"
            >
              {submitting ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <MessageSquarePlus size={16} className="mr-2" />
              )}
              Submit Feedback
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
